import { useState, type FormEvent } from 'react';
import { useMutation } from '@tanstack/react-query';
import { apiClient } from '@/api/client';
import { Button } from '@/components/ui/Button';
import { useVisitorStore } from '@/stores/visitorStore';
import { useAnalytics } from '@/hooks/useAnalytics';
import type { SectionProps } from '@/types';

export function FeedbackSection({ business, config }: SectionProps) {
  const title = (config.title as string) || 'Share your feedback';
  const subtitle =
    (config.subtitle as string) ||
    'Your review helps other customers and improves our service';

  const visitorFingerprint = useVisitorStore((s) => s.visitorFingerprint);
  const { track } = useAnalytics(business.id);

  const [rating, setRating] = useState(0);
  const [hoverRating, setHoverRating] = useState(0);
  const [wouldVisitAgain, setWouldVisitAgain] = useState<'yes' | 'maybe' | 'no' | ''>('');
  const [improvementText, setImprovementText] = useState('');
  const [submitted, setSubmitted] = useState(false);

  const mutation = useMutation({
    mutationFn: (body: {
      rating: number;
      would_visit_again: string;
      improvement_text?: string;
      visitor_fingerprint?: string;
    }) =>
      apiClient<{ id: string; rating: number }>(
        `/public/businesses/${business.id}/feedback`,
        { method: 'POST', body },
      ),
    onSuccess: () => {
      setSubmitted(true);
      track('feedback_submitted', { rating });
    },
  });

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    if (!rating || !wouldVisitAgain) return;

    mutation.mutate({
      rating,
      would_visit_again: wouldVisitAgain,
      improvement_text: improvementText || undefined,
      visitor_fingerprint: visitorFingerprint || undefined,
    });
  };

  if (submitted) {
    return (
      <section className="section-card text-center">
        <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-emerald-100 text-emerald-600">
          <svg className="h-8 w-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
          </svg>
        </div>
        <h2 className="mt-4 text-xl font-semibold text-slate-900">Thank you!</h2>
        <p className="mt-2 text-slate-600">Your feedback has been submitted.</p>
      </section>
    );
  }

  return (
    <section className="section-card">
      <h2 className="text-xl font-semibold text-slate-900">{title}</h2>
      <p className="mt-1 text-sm text-slate-500">{subtitle}</p>

      <form onSubmit={handleSubmit} className="mt-6 space-y-6">
        <div>
          <label className="block text-sm font-medium text-slate-700">Your rating</label>
          <div className="mt-2 flex gap-1" role="group" aria-label="Rating">
            {[1, 2, 3, 4, 5].map((star) => (
              <button
                key={star}
                type="button"
                className="p-1 transition hover:scale-110"
                onMouseEnter={() => setHoverRating(star)}
                onMouseLeave={() => setHoverRating(0)}
                onClick={() => setRating(star)}
                aria-label={`Rate ${star} stars`}
              >
                <svg
                  className={`h-8 w-8 ${
                    star <= (hoverRating || rating) ? 'text-amber-400' : 'text-slate-300'
                  }`}
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                </svg>
              </button>
            ))}
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-slate-700">Would you visit again?</label>
          <div className="mt-2 flex flex-wrap gap-2">
            {(['yes', 'maybe', 'no'] as const).map((option) => (
              <button
                key={option}
                type="button"
                onClick={() => setWouldVisitAgain(option)}
                className={`rounded-full px-4 py-2 text-sm font-medium capitalize transition ${
                  wouldVisitAgain === option
                    ? 'bg-brand-primary text-white'
                    : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                }`}
              >
                {option}
              </button>
            ))}
          </div>
        </div>

        <div>
          <label htmlFor="improvement" className="block text-sm font-medium text-slate-700">
            Suggestions (optional)
          </label>
          <textarea
            id="improvement"
            rows={3}
            value={improvementText}
            onChange={(e) => setImprovementText(e.target.value)}
            maxLength={2000}
            placeholder="Tell us what we can improve..."
            className="mt-2 w-full rounded-brand border border-slate-300 px-3 py-2 text-sm focus:border-brand-primary focus:outline-none focus:ring-1 focus:ring-brand-primary"
          />
        </div>

        {mutation.isError && (
          <p className="text-sm text-red-600">
            {(mutation.error as Error).message || 'Failed to submit feedback. Please try again.'}
          </p>
        )}

        <Button type="submit" isLoading={mutation.isPending} disabled={!rating || !wouldVisitAgain}>
          Submit feedback
        </Button>
      </form>
    </section>
  );
}
